import {
    ClaimWorkflowConfiguration,
    ClaimWorkflowConfigurationOptions,
    CoreFetch,
    CreateClaimWorkflowConfigurationRequest,
    WorkerApi,
} from '../../api';
import { isDemoModeActive } from '../auth/demo-mode.utils';

export type WorkerConfigurationApiMode = 'provider' | 'support';

/**
 * What Save stored in demo mode (FE-82) — `undefined` until the first Save.
 *
 * Module state on purpose, like every other piece of demo data: it lasts for the session. Leaving the Workers
 * page and coming back shows what was saved; a full reload starts from the demo configuration again.
 */
let demoSavedConfiguration: ClaimWorkflowConfiguration | undefined;

/** A deep copy, so the store's observables never alias what is kept here. The config is plain JSON. */
const copyConfiguration = (configuration: ClaimWorkflowConfiguration): ClaimWorkflowConfiguration =>
    JSON.parse(JSON.stringify(configuration));

export async function fetchClaimWorkflowConfiguration(
    apiMode: WorkerConfigurationApiMode,
): Promise<ClaimWorkflowConfiguration> {
    if (isDemoModeActive()) {
        if (demoSavedConfiguration) {
            return copyConfiguration(demoSavedConfiguration);
        }

        // What a configured production account has saved: every workflow status at the server's own
        // default, plus the two automation workers' rules (FE-82).
        //
        // The defaults are SAVED here rather than left for the page to fill in. `worker.store.ts`
        // fills unset option keys into the DRAFT only, so a demo configuration without them would
        // open as unsaved changes — a lit Save button on a page nobody touched.
        //
        // The rules are client-side objects keyed on codes the demo catalogue genuinely contains
        // (`worker-catalog.demo.ts`); see `worker-config.demo.ts`. Without them the Workers page showed
        // both workers at "0 rules", which is the screen Jordan singled out (2026-09-09).
        //
        // `createDate` is LOAD-BEARING, not filler. `hasPersistedConfiguration` is
        // `Boolean(configuration?.createDate) || Object.keys(savedConfig).length > 0`, and a
        // truthy value is what makes `worker.store.ts` skip `createConfigurationWithDefaults()`.
        // Drop it as decoration and the demo Workers page fires a POST that the request boundary
        // then refuses, surfacing as a save-error banner on a page nobody touched.
        // `await import`, never a static import: a value import of a demo fixture pulls it into
        // `main.js` for every customer, and `demo-bundle.coverage.test.ts` fails the build on it
        // repo-wide. This function is already async, so the lazy chunk costs nothing.
        const { buildDemoWorkflowConfig } = await import('../demo/worker-config.demo');
        return {
            active: true,
            // Cast, not a model change: the generated client types every config value as `object`
            // while the domain type `WorkflowConfigMap` uses primitives, and the real API path has
            // the same mismatch. Editing the generated model to fit would invent a wire contract
            // (NS-16), so the difference is absorbed here.
            config: buildDemoWorkflowConfig(apiMode) as unknown as ClaimWorkflowConfiguration['config'],
            createdBy: 'Demo',
            createDate: new Date().toISOString(),
        };
    }

    const api = CoreFetch<WorkerApi>(WorkerApi);

    if (apiMode === 'provider') {
        return api.getClaimWorkflowConfigurationProvider();
    }

    return api.getClaimWorkflowConfiguration();
}

/**
 * The create-or-replace POST of the whole workflow configuration.
 *
 * In demo mode nothing is sent (NS-14). The body replaces the configuration kept in memory, and comes back
 * the way the endpoint returns it (FE-82). Without this branch Save reached the API client, which demo mode
 * refuses — and once the demo showed every workflow setting, Save was the obvious thing to try.
 */
export async function postClaimWorkflowConfiguration(
    apiMode: WorkerConfigurationApiMode,
    requestBody: CreateClaimWorkflowConfigurationRequest['requestBody'],
): Promise<ClaimWorkflowConfiguration> {
    if (isDemoModeActive()) {
        const current = await fetchClaimWorkflowConfiguration(apiMode);
        demoSavedConfiguration = copyConfiguration({
            ...current,
            config: requestBody as unknown as ClaimWorkflowConfiguration['config'],
        });
        return copyConfiguration(demoSavedConfiguration);
    }

    const api = CoreFetch<WorkerApi>(WorkerApi);

    if (apiMode === 'provider') {
        return api.createClaimWorkflowConfigurationProvider({ requestBody });
    }

    return api.createClaimWorkflowConfiguration({ requestBody });
}

export async function fetchClaimWorkflowConfigurationOptions(
    apiMode: WorkerConfigurationApiMode,
): Promise<ClaimWorkflowConfigurationOptions[]> {
    if (isDemoModeActive()) {
        // The server's own enumeration of workflow statuses and their settings, verbatim (FE-82).
        //
        // This used to return `[]`, and that was the NS-4 call: the options are the configuration
        // CONTROLS, only the server enumerates them, and a made-up set would show a prospect switches
        // this product does not have. That reasoning still holds — which is why this is not a made-up
        // set. It is `GET /provider/worker/configuration/options` exactly as the API returned it
        // (sandbox, 2026-09-14), and production returned the same 29 statuses and 58 settings that
        // day. Jordan asked for the Workers page "same as prod has" (2026-09-11), and an empty status
        // list was the one part of it the demo could not show.
        //
        // A snapshot — `worker-options.demo.ts` says how to re-capture it. Copied per call so the
        // store's observable never aliases the module's constant.
        const { DEMO_WORKER_CONFIGURATION_OPTIONS } = await import('../demo/worker-options.demo');
        return DEMO_WORKER_CONFIGURATION_OPTIONS.map(status => ({
            ...status,
            options: (status.options || []).map(option => ({ ...option })),
        }));
    }

    const api = CoreFetch<WorkerApi>(WorkerApi);

    if (apiMode === 'provider') {
        return api.getClaimWorkflowConfigurationOptionsProvider();
    }

    return api.getClaimWorkflowConfigurationOptions();
}
